// Define the game rules for this specific game

import { GameAssets } from "./shared/util/GameAssets";
import { InfoPopup } from "./shared/UI/InfoPopup";
import { GlobalState } from "./shared/util/GlobalState";
import { paytable } from "./server/DataParser";

class SymbolPayBulletList {
  public bulletListParent: HTMLElement;
  private _bulletListPoints: HTMLElement[] = [];
  private _paytableKey: string;

  public constructor(
    paytableKey: string,
    symbolAmount: string[],
    name?: string,
    showBullets?: boolean,
    fontSize?: string,
    padding?: string,
    textAlign?: "center" | "left",
  ) {
    this._paytableKey = paytableKey;

    const bulletList: HTMLElement = document.createElement("ul");

    Object.assign(bulletList.style, {
      display: "block",
      padding: padding ?? "0px",
      margin: "0",
      position: "relative",
      fontSize: fontSize ?? "0.4em",
      listStyleType: showBullets ? "disc" : "none",
      color: "white",
      textAlign: textAlign ?? "center",
      zIndex: "9998",
    });

    // Keep the name separate from the actual symbol paylist
    if (name || name !== "") {
      const bulletPoint: HTMLElement = document.createElement("li");
      bulletPoint.innerHTML = name!;
      bulletList.appendChild(bulletPoint);
    }

    for (const amount of symbolAmount) {
      const bulletPoint: HTMLElement = document.createElement("li");
      bulletPoint.innerHTML = `${amount}: <span style='color: orange'></span>`;
      bulletList.appendChild(bulletPoint);
      this._bulletListPoints.push(bulletPoint);
    }

    const updatePayouts = (): void => {
      // console.log(GameEngine.getInstance().getFormatter().format(GlobalState.getInstance().bet));
      const bet: number = GlobalState.getInstance().bet;
      const symbolPayouts = paytable[this._paytableKey];

      for (let i: number = 0; i < this._bulletListPoints.length; ++i) {
        const bulletPoint = this._bulletListPoints[i];
        const payout = (symbolPayouts[i] * bet).toFixed(2);

        const span = bulletPoint.querySelector("span");
        if (span) {
          span.textContent = payout;
        }
      }
    };

    updatePayouts();

    document.addEventListener("updatedBet", () => {
      updatePayouts();
    });

    this.bulletListParent = bulletList;
  }
}

// Can be re-styled and repurposed for other games
export const getGameRuleHtmlElement: () => HTMLElement = () => {
  const root: HTMLElement = document.createElement("div");

  Object.assign(root.style, {
    display: "flex",
    flexDirection: "column",
    // boxSizing: "border-box",
    alignItems: "center",
    padding: "20px",
    position: "relative",
    overflowY: "scroll",
    overflowX: "hidden",
    width: "100%",
    height: "100%",
    zIndex: "9998",
  });

  const createHorizontalRuler: () => HTMLElement = () => {
    let horizontalRuler: HTMLElement = document.createElement("hr");

    Object.assign(horizontalRuler.style, {
      // Ensure flex layout won't shrink our hr-s
      flexShrink: "0",
      width: "100%",
      height: "2px",
      background:
        "linear-gradient(to right, #00000000 0%, #555 33%, #555 66%, #00000000 100%)",
      border: "0px",
      zIndex: "9998",
    });

    return horizontalRuler;
  };

  const createHeader: (text: string) => HTMLElement = (text: string) => {
    const header: HTMLElement = document.createElement("h4");

    Object.assign(header.style, {
      display: "block",
      position: "relative",
      margin: "0.25em 0em 0.25em 0em",
      color: "rgb(255, 133, 49)",
      zIndex: "9998",
    });

    header.textContent = text.replace(/\s+/g, " ");

    return header;
  };

  const createParagraph: (text: string) => HTMLElement = (text: string) => {
    const paragraph: HTMLElement = document.createElement("p");

    Object.assign(paragraph.style, {
      display: "block",
      position: "relative",
      fontSize: "0.7em",
      textAlign: "center",
      color: "white",
      zIndex: "9998",
    });

    text = text.trim();
    paragraph.innerHTML = text;

    return paragraph;
  };

  const createBulletList: (
    points: string[],
    showBullets?: boolean,
    padding?: string,
    textAlign?: "left" | "center",
  ) => HTMLElement = (
    points: string[],
    showBullets: boolean = true,
    padding: string = "0",
    textAlign: "left" | "center" = "left",
  ) => {
    const bulletList: HTMLElement = document.createElement("ul");

    Object.assign(bulletList.style, {
      display: "block",
      padding: padding,
      margin: "0",
      position: "relative",
      fontSize: "0.7em",
      listStyleType: showBullets ? "disc" : "none",
      color: "white",
      textAlign: textAlign,
      zIndex: "9998",
    });

    points.forEach((point: string) => {
      const bulletPoint: HTMLElement = document.createElement("li");
      bulletPoint.innerHTML = point;
      bulletList.appendChild(bulletPoint);
    });

    return bulletList;
  };

  const createImage: (
    atlasTexturePath: string,
    spriteName: string,
    width?: string,
    height?: string,
    margin?: string,
  ) => HTMLImageElement = (
    path: string,
    width: string = "auto",
    height: string = "6em",
    margin: string = "0",
  ) => {
    const image: HTMLImageElement = document.createElement("img");

    Object.assign(image.style, {
      display: "block",
      position: "relative",
      margin: margin,
      width: width,
      height: height,
      aspectRatio: "auto",
      objectFit: "cover",
      zIndex: "9998",
    });

    image.src = path;

    return image;
  };

  const createAtlasImage = (
    spriteName: string,
    atlas: any,
    width: string = "auto",
    height: string = "6em",
    margin: string = "0",
  ) => {
    const div = document.createElement("div");
    const atlasData: any = atlas;
    const spriteData: any = atlasData.textures[spriteName];

    if (!spriteData) {
      console.error(`Sprite ${spriteName} not found in atlas`);
      return div;
    }

    const updateStyle = () => {
      const fontSize: number = parseFloat(
        InfoPopup.getComputedStyle().fontSize,
      );
      // console.log(fontSize);
      const frame = spriteData.frame;

      const atlasWidth = atlasData.textureSource.pixelWidth;
      const atlasHeight = atlasData.textureSource.pixelHeight;

      // Calculate sprite aspect ratio
      const spriteAspectRatio = frame.width / frame.height;

      // Convert em to pixels
      const containerHeight = parseFloat(height) * fontSize;

      // Calculate width based on height and sprite aspect ratio
      let containerWidth: number;
      let actualWidth: string;

      if (width === "auto") {
        containerWidth = containerHeight * spriteAspectRatio;
        actualWidth = `${containerWidth / fontSize}em`; // Convert back to em
      } else {
        containerWidth = parseFloat(width) * fontSize;
        actualWidth = width;
      }

      // Calculate scale to fit sprite in container
      const scaleX = containerWidth / frame.width;
      const scaleY = containerHeight / frame.height;
      const scale = Math.min(scaleX, scaleY); // Maintain aspect ratio

      // Calculate background position (scaled)
      const bgX = -frame.x * scale;
      const bgY = -frame.y * scale;

      Object.assign(div.style, {
        display: "block",
        flexShrink: "0",
        position: "relative",
        margin: margin,
        width: actualWidth,
        height: height,
        backgroundImage: `url(${spriteData._source._sourceOrigin})`,
        backgroundPosition: `${bgX}px ${bgY}px`,
        backgroundSize: `${atlasWidth * scale}px ${atlasHeight * scale}px`,
        backgroundRepeat: "no-repeat",
        zIndex: "9998",
      });
    };

    // Update the style upon creation, and upon changing window size
    updateStyle();
    window.addEventListener("resize", () => {
      updateStyle();
    });

    return div;
  };
  // const createGrid: (
  //   elements: HTMLElement[],
  //   rows: number,
  //   columns: number,
  // ) => HTMLElement = (
  //   elements: HTMLElement[],
  //   rows: number,
  //   columns: number,
  // ) => {
  //   const grid: HTMLElement = document.createElement("div");

  //   Object.assign(grid.style, {
  //     display: "grid",
  //     justifyContent: "center",
  //     gap: "0px",
  //     gridTemplateRows: `repeat(${rows}, 1fr)`,
  //     gridTemplateColumns: `repeat(${columns}, 1fr)`,
  //     position: "relative",
  //     zIndex: "9998",
  //   });

  //   elements.forEach((element: HTMLElement) => {
  //     grid.appendChild(element);
  //   });

  //   return grid;
  // };

  const createHorizontalElementSequence: (
    elements: HTMLElement[],
    width: string,
    elementWidth: string,
  ) => HTMLElement = (
    elements: HTMLElement[],
    width: string,
    elementWidth: string,
  ) => {
    const container: HTMLElement = document.createElement("div");

    Object.assign(container.style, {
      display: "flex",
      width: width,
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "baseline",
      position: "relative",
      zIndex: "9998",
    });

    elements.forEach((element: HTMLElement) => {
      Object.assign(element.style, {
        width: elementWidth,
      });
      container.appendChild(element);
    });

    return container;
  };

  const createCard: (elements: HTMLElement[]) => HTMLElement = (
    elements: HTMLElement[],
  ) => {
    const card: HTMLElement = document.createElement("div");

    Object.assign(card.style, {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      position: "relative",
      boxSizing: "border-box",
      zIndex: "9998",
    });

    elements.forEach((element: HTMLElement) => {
      card.appendChild(element);
    });

    return card;
  };

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Paytable"));
  root.appendChild(
    createParagraph(
      `Temple of Aphrodite is a 6x5 video slot.<br>
        The game features Scatter Symbols, and Free Spins Bonus.<br> 
        The maximum win is 6000x bet!`,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Scatter Symbol"));
  root.appendChild(
    createAtlasImage("Scatter.webp", GameAssets.textures.symbolAtlas),
  );
  root.appendChild(
    createParagraph(
      `Scatter symbol is present on all reels, and pays on any position.<br> `,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Tumble Feature"));
  root.appendChild(
    createParagraph(
      `The Tumble Feature means that after every spin, winning combinations are paid and all winning symbols disappear. The remaining symbols fall to the bottom of the screen and the empty positions are replaced with symbols coming from above.<br>
        Tumbling will continue until no more winning combinations appear as a reesult of a tumble. There's no limit to the number of possible tumbles.<br>
        All wins are added to the player's balance after all the tumbles results from a base spin have been played.`,
    ),
  );
  root.appendChild(
    createImage(GameAssets.textures.multipliers.label, "100%", "3em"),
  );
  root.appendChild(
    createParagraph(
      `These are the multiplier symbols. They are present on all reels and can hit randomly during spins and tumbles in both base game and Free Spins.<br>
        When the tumbling sequence ends, the values of all Multiplier symbols on the screen are added togeher and the total win of the sequence if multiplied by the final value.`,
    ),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Free Spins Bonus"));
  root.appendChild(
    createParagraph(
      `The Free Spins feature is awarded when 4 or more Scatter symbols hit anywhere on the screen.<br>
       The round starts with 5 free spins.<br>
       During the Free Spins round, whenever a Multiplier symbol hits and the spin results in a win, the Multiplier value gets added to the total multiplier. For the whole duration of the round, whenever any new Multiplier symbol hits and results in a win, the total multiplier value is also used to multiply the win.<br>
        Whenever 3 or more Scatter symbols hit during the Free Spins round, 5 additional free spins are awarded.<br>
        Special reels are in play during the Free Spins round.`,
    ),
  );

  // rgb(255, 133, 49) orange
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "spinButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Spin button."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "buyButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Cheat button."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "soundOn.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(["Sound toggle on/off."], false, "0", "center"),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "autoplayButton.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(
        ["Allows specifying the amount of times autoplay will be executed."],
        false,
        "0",
        "center",
      ),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createAtlasImage(
        "fastOn.webp",
        GameAssets.textures.uiAtlas,
        "auto",
        "3em",
        "0.5em",
      ),
      createBulletList(
        ["Allows specifying the amount of times autoplay will be executed."],
        false,
        "0",
        "center",
      ),
    ]),
  );
  root.appendChild(createHorizontalRuler());
  root.appendChild(
    createCard([
      createImage(GameAssets.textures.betDemo.label, "auto", "2em", "0.5em"),
      createBulletList(
        [
          "Allows setting a custom bet amount. Cannot be changed during a spin. Max bet allowed is 100 EUR.",
        ],
        false,
        "0",
        "center",
      ),
    ]),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Symbols Pay (EUR)"));
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "Scatter.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "5em",
            "0.2em",
          ),
          new SymbolPayBulletList(
            "10",
            ["4", "5", "6"],
            "Scatter",
            false,
            "0.6em",
          ).bulletListParent,
        ]),
      ],
      "100%",
      "20%",
    ),
  );
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "RedStone.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("9", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "GreenStone.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("8", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "YellowStone.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("7", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "PinkStone.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("6", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "BlueStone.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("5", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
      ],
      "100%",
      "20%",
    ),
  );
  root.appendChild(
    createHorizontalElementSequence(
      [
        createCard([
          createAtlasImage(
            "Apple.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("4", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "Rose.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("3", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "Swan.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("2", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
        createCard([
          createAtlasImage(
            "Mirror.webp",
            GameAssets.textures.symbolAtlas,
            "auto",
            "3em",
            "0.2em",
          ),
          new SymbolPayBulletList("1", ["8-9", "10-11", "12-30"], "", false)
            .bulletListParent,
        ]),
      ],
      "100%",
      "25%",
    ),
  );
  root.appendChild(
    createParagraph("A symbol's pay reflects current bet configuration."),
  );

  root.appendChild(createHorizontalRuler());
  root.appendChild(createHeader("Rules & Information"));
  root.appendChild(
    createBulletList(
      [
        "Symbols pay anywhere.",
        "All wins are multiplied by base bet.",
        "All values are expressed as actual wins in coins.",
        "When winning with multiple symbols, all the wins are added to the total win.",
        "Free spins win is awarded to the player after the round completes.",
        "Free spins total win in the history contains the whole win of the cycle.",
        "The theortical RTP of this game is 96.50%.",
        "The minimum bet is 20 pounds.",
        "The maximum bet is 125 pounds.",
        "Malfunction voids all pays and plays.",
      ],
      true,
      "1em",
    ),
  );

  return root;
};
